/**
 * Moteur de correlation repas -> symptomes.
 *
 * Module **pur** : il ne connait ni React ni Dexie, ne lit aucune horloge sans
 * qu'on la lui passe, et n'utilise que des generateurs aleatoires a graine fixe.
 * C'est ce qui permet de le valider sur des jeux synthetiques a signal connu
 * (cf. `tests/analysis/engine.test.ts`).
 *
 * L'enjeu n'est pas de trouver des associations — avec 200 ingredients et
 * 9 symptomes, on en trouve toujours — mais de ne pas en trouver quand il n'y
 * en a pas. D'ou les quatre garde-fous : effectifs minimaux, correction FDR,
 * test de permutation respectant l'auto-correlation, et detection des
 * expositions indissociables.
 */

import {
  FODMAP_FAMILIES,
  FODMAP_FAMILY_LABELS,
  FODMAP_LEVEL_WEIGHT,
  INGREDIENT_CATEGORY_LABELS,
  SYMPTOM_TYPES,
  quantiteEnGrammes,
  type DailyLog,
  type Id,
  type Ingredient,
  type Meal,
  type Recipe,
  type SymptomEpisode,
  type SymptomType,
} from '../domain/types'
import { HEURE_MS, JOUR_MS, dayKeyOf, epoch } from '../domain/time'
import {
  benjaminiHochberg,
  fisherExactBilateral,
  intervalleWilson,
  mannWhitney,
  mediane,
  quantile,
  rng,
} from './stats'
import {
  calculerIssues,
  decalerEpisodes,
  prefixeMaxFin,
  trierEpisodes,
  type EpisodeSimple,
  type IssuesSymptome,
} from './fenetres'
import {
  OPTIONS_DEFAUT,
  type Association,
  type Contingence,
  type Exposition,
  type FenetreAnalyse,
  type NiveauAnalyse,
  type OptionsAnalyse,
  type QualiteDonnees,
  type ResultatAnalyse,
  type ResultatDose,
  type Stratification,
  type Verdict,
} from './types'

export interface EntreeAnalyse {
  repas: readonly Meal[]
  /**
   * Poussees deja reconstituees par `analysis/evenements.ts`.
   *
   * Le moteur ignore volontairement la distinction sensation / selle : la
   * derivation se fait en amont, ce qui le laisse inchange — et ses garde-fous
   * avec lui.
   */
  episodes: readonly SymptomEpisode[]
  journaux: readonly DailyLog[]
  ingredients: readonly Ingredient[]
  /** Recettes, pour tester chacune comme exposition a part entiere. */
  recettes?: readonly Recipe[]
  /** Instant de reference (ms epoch). Injecte pour rendre les tests deterministes. */
  maintenant?: number
  options?: Partial<OptionsAnalyse>
}

// ---------------------------------------------------------------------------
// Construction des expositions
// ---------------------------------------------------------------------------

/** Cle stable pour identifier une exposition, tous niveaux confondus. */
function cleExposition(niveau: NiveauAnalyse, id: string): string {
  return `${niveau}:${id}`
}

/**
 * Horizons de cumul de la charge FODMAP, en heures.
 *
 * Sous charge controlee en aveugle, la douleur monte au jour 1 pour les polyols,
 * au jour 2 pour les fructanes et les GOS, au jour 3 pour le lactose. Ce n'est
 * donc pas la latence d'un repas isole : c'est le temps qu'il faut a une
 * exposition **repetee** pour franchir un seuil. Tester « ce repas contenait-il
 * de l'oignon ? » teste la mauvaise variable ; la bonne est une charge glissante.
 */
const HORIZONS_CHARGE_H = [24, 48] as const

/**
 * Plafond de tirages de permutation, pour borner le temps de calcul.
 *
 * Le moteur tourne dans le thread principal d'un telephone : au-dela, l'analyse
 * gele l'interface. Si ce plafond empeche le verdict d'etre atteignable, le
 * moteur le **dit** plutot que de rendre silencieusement des « a surveiller ».
 */
const PERMUTATIONS_MAX = 20_000

/**
 * Somme des valeurs portees par les repas de l'intervalle `(t_i - horizon, t_i]`.
 *
 * Deux pointeurs sur des instants deja tries : O(n).
 */
function sommeGlissante(
  tRepas: Float64Array,
  valeurs: Float64Array,
  horizonMs: number,
): Float64Array {
  const n = tRepas.length
  const out = new Float64Array(n)
  let somme = 0
  let debut = 0
  for (let i = 0; i < n; i++) {
    somme += valeurs[i]!
    while (debut < i && tRepas[debut]! <= tRepas[i]! - horizonMs) {
      somme -= valeurs[debut]!
      debut++
    }
    out[i] = somme
  }
  return out
}

/**
 * Ajoute les expositions « charge cumulee » : une par famille FODMAP et par
 * horizon, plus la charge totale.
 *
 * ## Pourquoi le tiers haut
 *
 * Le moteur raisonne sur des tables de contingence, donc sur une exposition
 * binaire. Le decoupage retenu est le tertile superieur de la distribution de
 * la charge glissante : il conserve un tiers des repas en exposes, ce qui donne
 * des effectifs exploitables sans dependre d'un seuil arbitraire en grammes.
 *
 * ## Pourquoi ce n'est pas du data snooping
 *
 * Le seuil est calcule **une fois sur les donnees observees, puis reste fixe**.
 * Cela ressemble a une selection informee par les donnees, et n'en est pas : le
 * test de permutation decale la chronologie des **symptomes**, jamais celle des
 * repas. La validite de l'hypothese nulle exige precisement que la definition de
 * l'exposition soit invariante d'un tirage a l'autre — ce qu'elle est ici.
 */
function ajouterChargesGlissantes(
  acc: Map<string, Exposition>,
  tRepas: Float64Array,
  chargeTotaleParRepas: Float64Array,
): void {
  const n = tRepas.length
  if (n === 0) return

  // Series denses : une par famille, plus le total. Le total est accumule a
  // part et non comme somme des familles — un ingredient rattache a deux
  // familles y serait sinon compte deux fois.
  const series: { cle: string; libelle: string; dense: Float64Array }[] = []

  for (const fam of FODMAP_FAMILIES) {
    const e = acc.get(cleExposition('famille', fam))
    if (!e) continue
    const dense = new Float64Array(n)
    e.repasIdx.forEach((idx, k) => {
      dense[idx] = e.charges[k] ?? 0
    })
    series.push({ cle: fam, libelle: FODMAP_FAMILY_LABELS[fam], dense })
  }

  let totalNonNul = false
  for (let i = 0; i < n; i++) {
    if (chargeTotaleParRepas[i]! > 0) {
      totalNonNul = true
      break
    }
  }
  if (totalNonNul) {
    series.push({ cle: 'totale', libelle: 'Charge FODMAP totale', dense: chargeTotaleParRepas })
  }

  for (const s of series) {
    for (const h of HORIZONS_CHARGE_H) {
      const glissante = sommeGlissante(tRepas, s.dense, h * HEURE_MS)
      const valeurs = Array.from(glissante)
      const seuil = quantile(valeurs, 2 / 3)

      const repasIdx: number[] = []
      const charges: number[] = []
      for (let i = 0; i < n; i++) {
        const v = glissante[i]!
        // `v > 0` rattrape le cas d'une famille rarement consommee, ou le
        // tertile vaut zero : « le tiers haut » y devient « tout ce qui est
        // non nul », ce qui reste la bonne dichotomie.
        if (v > seuil && v > 0) {
          repasIdx.push(i)
          charges.push(v)
        }
      }
      if (repasIdx.length === 0) continue

      const cle = cleExposition('charge', `${s.cle}-${h}h`)
      acc.set(cle, {
        cle,
        libelle: `${s.libelle} sur ${h} h`,
        niveau: 'charge',
        repasIdx,
        charges,
      })
    }
  }
}

function construireExpositions(
  repasTries: readonly Meal[],
  tRepas: Float64Array,
  ingredients: readonly Ingredient[],
  journaux: readonly DailyLog[],
  recettes: readonly Recipe[],
): Exposition[] {
  const parId = new Map<Id, Ingredient>(ingredients.map((i) => [i.id, i]))

  const acc = new Map<string, Exposition>()
  const chargeTotaleParRepas = new Float64Array(repasTries.length)
  const ajouter = (
    niveau: NiveauAnalyse,
    id: string,
    libelle: string,
    idxRepas: number,
    charge: number,
  ) => {
    const cle = cleExposition(niveau, id)
    let e = acc.get(cle)
    if (!e) {
      e = { cle, libelle, niveau, repasIdx: [], charges: [] }
      acc.set(cle, e)
    }
    const dernier = e.repasIdx.length - 1
    if (dernier >= 0 && e.repasIdx[dernier] === idxRepas) {
      // Meme repas : on cumule la charge (plusieurs ingredients d'une meme famille).
      e.charges[dernier] = (e.charges[dernier] ?? 0) + charge
    } else {
      e.repasIdx.push(idxRepas)
      e.charges.push(charge)
    }
  }

  repasTries.forEach((m, idx) => {
    // Deduplication : un meme ingredient peut venir de deux recettes du repas.
    const grammesParIngredient = new Map<Id, number>()
    for (const item of m.ingredientsResolus) {
      const g = quantiteEnGrammes(item)
      grammesParIngredient.set(item.ingredientId, (grammesParIngredient.get(item.ingredientId) ?? 0) + g)
    }

    for (const [ingId, grammes] of grammesParIngredient) {
      const ing = parId.get(ingId)
      if (!ing) continue

      ajouter('ingredient', ing.id, ing.nom, idx, grammes)
      ajouter('categorie', ing.categorie, INGREDIENT_CATEGORY_LABELS[ing.categorie], idx, grammes)

      // La charge d'une famille pondere la quantite par le niveau FODMAP :
      // 200 g d'un aliment « eleve » ne pese pas comme 200 g d'un « faible ».
      const poids = FODMAP_LEVEL_WEIGHT[ing.niveauFodmap]
      // Comptee une fois par ingredient, quel que soit son nombre de familles :
      // sommer les familles double-compterait une pomme (fructose + polyols).
      chargeTotaleParRepas[idx] = (chargeTotaleParRepas[idx] ?? 0) + grammes * poids
      for (const fam of ing.famillesFodmap) {
        ajouter('famille', fam, FODMAP_FAMILY_LABELS[fam], idx, grammes * poids)
      }
    }
  })

  // Charges glissantes : construites apres la boucle, car elles agregent les
  // charges par famille que celle-ci vient d'accumuler.
  ajouterChargesGlissantes(acc, tRepas, chargeTotaleParRepas)

  // Recettes : une recette est une exposition a part entiere, testee avec
  // exactement les memes garde-fous qu'un ingredient. Elle est evidemment
  // correlee a ses propres ingredients — la detection des expositions
  // indissociables s'en charge et le signale.
  const nomRecette = new Map(recettes.map((r) => [r.id, r.nom]))
  repasTries.forEach((m, idx) => {
    for (const src of m.sources) {
      if (src.kind !== 'recipe') continue
      ajouter(
        'recette',
        src.recipeId,
        nomRecette.get(src.recipeId) ?? 'Recette supprimée',
        idx,
        src.portions,
      )
    }
  })

  // Facteurs de vie : rattaches au jour du repas.
  const parJour = new Map<string, DailyLog>(journaux.map((j) => [j.date, j]))
  repasTries.forEach((m, idx) => {
    const j = parJour.get(dayKeyOf(m.at))
    if (!j) return
    if (j.stress !== null && j.stress >= 7) {
      ajouter('facteur', 'stress-eleve', 'Stress eleve (>= 7/10)', idx, j.stress)
    }
    if (j.heuresSommeil !== null && j.heuresSommeil < 6) {
      ajouter('facteur', 'sommeil-court', 'Nuit courte (< 6 h)', idx, 6 - j.heuresSommeil)
    }
    if (j.qualiteSommeil !== null && j.qualiteSommeil <= 4) {
      ajouter('facteur', 'sommeil-mauvais', 'Mauvais sommeil (<= 4/10)', idx, 5 - j.qualiteSommeil)
    }
  })

  return [...acc.values()]
}

// ---------------------------------------------------------------------------
// Contingence
// ---------------------------------------------------------------------------

interface Compte {
  contingence: Contingence
  episodes: Set<number>
}

function compter(expo: Exposition, issues: IssuesSymptome): Compte {
  const { eligible, onset, episodeIdx, nbEligibles, nbOnsets } = issues
  let a = 0
  let nExposes = 0
  const episodes = new Set<number>()

  for (const idx of expo.repasIdx) {
    if (!eligible[idx]) continue
    nExposes++
    if (onset[idx]) {
      a++
      const e = episodeIdx[idx]!
      if (e >= 0) episodes.add(e)
    }
  }

  const b = nExposes - a
  const c = nbOnsets - a
  const d = nbEligibles - nExposes - c
  return { contingence: { a, b, c, d }, episodes }
}

function risques(ct: Contingence) {
  const nExp = ct.a + ct.b
  const nNon = ct.c + ct.d
  const rExp = nExp > 0 ? ct.a / nExp : 0
  const rNon = nNon > 0 ? ct.c / nNon : 0
  return { rExp, rNon, nExp, nNon }
}

/**
 * Statistique du test de permutation : la **difference de risque**.
 *
 * Preferee au risque relatif, qui vaut 0 ou l'infini des qu'une case est vide —
 * ce qui arrive constamment sous permutation et rendrait la distribution nulle
 * inexploitable. La difference reste toujours dans [-1, 1].
 */
function differenceRisque(ct: Contingence): number {
  const { rExp, rNon } = risques(ct)
  return rExp - rNon
}

// ---------------------------------------------------------------------------
// Analyse principale
// ---------------------------------------------------------------------------

export function analyser(entree: EntreeAnalyse): ResultatAnalyse {
  const demandees = { ...OPTIONS_DEFAUT, ...entree.options }
  // Un jeu de fenetres vide desactiverait toute l'analyse en silence : on
  // retombe sur les fenetres par defaut, et `options` porte le jeu reellement
  // utilise pour que le resultat soit auto-descriptif.
  const fenetres = demandees.fenetres.length > 0 ? demandees.fenetres : OPTIONS_DEFAUT.fenetres
  const options: OptionsAnalyse = { ...demandees, fenetres }
  const episodes = entree.episodes
  const maintenant = entree.maintenant ?? Date.now()

  const repasTries = [...entree.repas].sort((x, y) => epoch(x.at) - epoch(y.at))
  const tRepas = new Float64Array(repasTries.length)
  repasTries.forEach((m, i) => {
    tRepas[i] = epoch(m.at)
  })

  const expositions = construireExpositions(
    repasTries,
    tRepas,
    entree.ingredients,
    entree.journaux,
    entree.recettes ?? [],
  )

  // Periode d'observation, utilisee par le decalage circulaire.
  const debutsEpisodes = episodes.map((e) => epoch(e.debutAt))
  const t0 = Math.min(
    tRepas.length ? tRepas[0]! : Infinity,
    debutsEpisodes.length ? Math.min(...debutsEpisodes) : Infinity,
  )
  const T = Number.isFinite(t0) ? Math.max(0, maintenant - t0) : 0

  const stressParJour = new Map<string, number>()
  for (const j of entree.journaux) {
    if (j.stress !== null) stressParJour.set(j.date, j.stress)
  }

  /** Ce qu'une fenetre donne pour un couple (exposition, symptome). */
  interface Mesure {
    fenetre: FenetreAnalyse
    compte: Compte
    issues: IssuesSymptome
    exploitable: boolean
    motif?: string
    pFisher: number
    /** Difference de risque **signee** : negative = exposition protectrice. */
    delta: number
  }

  /**
   * Une ligne = un couple (exposition, symptome), **toutes fenetres reunies**.
   *
   * La fenetre n'est pas un test de plus : c'est une dimension de recherche. On
   * pose une seule question — « y a-t-il un signal, et dans quelle fenetre ? » —
   * dont la statistique est le maximum de |difference de risque| sur les
   * fenetres exploitables. Le test de permutation prend le meme maximum a chaque
   * tirage, et **paie donc automatiquement le cout de cette recherche**.
   */
  interface Ligne {
    expo: Exposition
    symptome: SymptomType
    mesures: Mesure[]
    /** Fenetres entrant dans le maximum : celles qui passent les effectifs. */
    retenues: Mesure[]
    /** Fenetre du maximum, ou la plus informative si aucune n'est exploitable. */
    meilleure: Mesure
    exploitable: boolean
    motif?: string
    pFisher: number
    statObs: number
    pPermutation: number | null
  }

  const lignes: Ligne[] = []
  const episodesParSymptome = new Map<SymptomType, EpisodeSimple[]>()

  // --- Phase A : contingence + Fisher, fenetre par fenetre, puis maximum ---
  //
  // La chronologie des episodes (`episodesTries`, `maxFin`) ne depend pas de la
  // fenetre : elle est calculee une fois par symptome et partagee. Seul
  // `calculerIssues` est rejoue par fenetre.
  for (const symptome of SYMPTOM_TYPES) {
    const bruts = episodes.filter((e) => e.type === symptome)
    if (bruts.length === 0) continue

    const episodesTries = trierEpisodes(
      bruts.map<EpisodeSimple>((e) => ({
        debut: epoch(e.debutAt),
        fin: e.finAt === null ? null : epoch(e.finAt),
      })),
    )
    const maxFin = prefixeMaxFin(episodesTries)

    const issuesParFenetre: { fenetre: FenetreAnalyse; issues: IssuesSymptome }[] = []
    for (const fenetre of fenetres) {
      const issues = calculerIssues(
        tRepas,
        episodesTries,
        maxFin,
        fenetre.debutH * HEURE_MS,
        fenetre.finH * HEURE_MS,
        maintenant,
      )
      if (issues.nbEligibles === 0 || issues.nbOnsets === 0) continue
      issuesParFenetre.push({ fenetre, issues })
    }
    if (issuesParFenetre.length === 0) continue

    episodesParSymptome.set(symptome, episodesTries)

    for (const expo of expositions) {
      const mesures: Mesure[] = issuesParFenetre.map(({ fenetre, issues }) => {
        const compte = compter(expo, issues)
        const ct = compte.contingence
        const nExp = ct.a + ct.b

        let motif: string | undefined
        if (nExp < options.minRepasExposes) {
          motif = `Seulement ${nExp} repas exploitable(s) contenant cette exposition (minimum ${options.minRepasExposes}).`
        } else if (ct.a < options.minEvenements) {
          motif = `Seulement ${ct.a} déclenchement(s) observé(s) après exposition (minimum ${options.minEvenements}).`
        } else if (ct.c + ct.d === 0) {
          motif = 'Cette exposition est présente dans tous les repas : aucune comparaison possible.'
        }

        const exploitable = motif === undefined
        return {
          fenetre,
          compte,
          issues,
          exploitable,
          motif,
          pFisher: exploitable ? fisherExactBilateral(ct.a, ct.b, ct.c, ct.d) : 1,
          delta: differenceRisque(ct),
        }
      })

      const retenues = mesures.filter((m) => m.exploitable)

      if (retenues.length === 0) {
        // Aucune fenetre exploitable : on garde la plus informative — celle qui
        // compte le plus de repas exposes — pour que le motif affiche soit
        // celui qui manque de le moins.
        const meilleure = mesures.reduce((best, m) =>
          m.compte.contingence.a + m.compte.contingence.b >
          best.compte.contingence.a + best.compte.contingence.b
            ? m
            : best,
        )
        lignes.push({
          expo,
          symptome,
          mesures,
          retenues,
          meilleure,
          exploitable: false,
          motif: meilleure.motif,
          pFisher: 1,
          statObs: 0,
          pPermutation: null,
        })
        continue
      }

      const meilleure = retenues.reduce((best, m) =>
        Math.abs(m.delta) > Math.abs(best.delta) ? m : best,
      )

      // Bonferroni sur les fenetres reellement retenues : le p de Fisher est
      // celui d'une fenetre choisie **apres avoir regarde les quatre**, et le
      // comparer tel quel a un seuil ignorerait cette recherche. Correction
      // volontairement grossiere — les fenetres se recouvrent, donc les tests
      // sont correles et Bonferroni sur-corrige — mais c'est le test de
      // permutation qui porte l'inference, celui-ci ne sert que de pre-filtre
      // et de garde-fou.
      const pMin = retenues.reduce((p, m) => Math.min(p, m.pFisher), 1)

      lignes.push({
        expo,
        symptome,
        mesures,
        retenues,
        meilleure,
        exploitable: true,
        pFisher: Math.min(1, pMin * retenues.length),
        statObs: Math.abs(meilleure.delta),
        pPermutation: null,
      })
    }
  }

  // --- Phase B : permutation, sur les seuls candidats pre-filtres ---
  //
  // Le decalage circulaire depend des episodes, donc du symptome mais **pas de
  // la fenetre** : on le calcule une fois par tirage et on l'evalue contre les
  // quatre fenetres. C'est ce partage qui evite de multiplier par quatre le
  // cout de la phase la plus chere du moteur.
  //
  // Le pre-filtre effectif est `max(seuilPrefiltre, seuilQ)` : une ligne dont le
  // p de Fisher depasse `seuilQ` ne peut pas atteindre `qFisher <= seuilQ`
  // (BH garantit q >= p), donc pas devenir une piste serieuse. La permuter
  // serait du calcul perdu, et ne pas la permuter ne change aucune q-value.
  let permutationsUtilisees = 0
  let plafondPermutationsAtteint = false

  if (T > 0 && options.permutations > 0) {
    const prefiltre = Math.max(options.seuilPrefiltre, options.seuilQ)

    const candidatsParSymptome = new Map<SymptomType, Ligne[]>()
    let nbCandidats = 0
    for (const symptome of episodesParSymptome.keys()) {
      const c = lignes.filter(
        (l) => l.symptome === symptome && l.exploitable && l.pFisher <= prefiltre,
      )
      if (c.length === 0) continue
      candidatsParSymptome.set(symptome, c)
      nbCandidats += c.length
    }

    // Nombre de tirages requis pour que le verdict reste *atteignable*.
    //
    // Une p-value de permutation ne peut pas descendre sous 1/(B+1). Corrigee
    // par BH sur la famille rembourree (phase C), la plus petite q-value
    // possible vaut donc `nbExploitables / (B+1)`. Si elle depasse `seuilQ`,
    // plus aucune piste ne peut etre declaree serieuse **quelle que soit la
    // force du signal** : une association a RR 2,9 et p de Fisher a 1e-16
    // ressortait « a surveiller », sans qu'aucun message ne l'explique.
    //
    // C'est le mode de panne le plus insidieux du moteur, parce qu'il se
    // declenche par simple croissance du journal : la taille de la famille
    // monte avec le nombre d'aliments distincts, et le verdict s'eteint sans
    // que rien ne change dans les donnees.
    const nbExploitables = lignes.reduce((n, l) => n + (l.exploitable ? 1 : 0), 0)
    const requis = Math.ceil(nbExploitables / options.seuilQ)
    const B = Math.min(Math.max(options.permutations, requis), PERMUTATIONS_MAX)
    plafondPermutationsAtteint = requis > PERMUTATIONS_MAX
    permutationsUtilisees = B

    const alea = rng(options.graine)

    for (const [symptome, candidats] of candidatsParSymptome) {
      const episodesTries = episodesParSymptome.get(symptome)!

      // Toutes les fenetres retenues par au moins un candidat encore actif.
      // `calculerIssues` n'est appele qu'une fois par fenetre et par tirage,
      // pour tous les candidats qui la partagent.
      const toutesFenetres = [...new Set(candidats.flatMap((l) => l.retenues.map((m) => m.fenetre)))]
      const rangFenetre = new Map(toutesFenetres.map((f, i) => [f, i]))

      // Indices, dans `toutesFenetres`, des fenetres entrant dans le maximum de
      // chaque candidat. Fige la definition de la statistique : le maximum
      // porte toujours sur les memes fenetres, tirage apres tirage.
      const fenetresDuMax = candidats.map((l) => l.retenues.map((m) => rangFenetre.get(m.fenetre)!))

      const extremes = new Array<number>(candidats.length).fill(0)
      const tirages = new Array<number>(candidats.length).fill(0)

      // --- Arret anticipe, exactement preservateur du verdict ---
      //
      // BH garantit q >= p. Des qu'un candidat a accumule assez de tirages
      // extremes pour que `p > seuilQ`, son verdict est scelle : il ne peut
      // plus etre declare piste serieuse, et continuer a tirer pour affiner une
      // p-value qu'on sait deja au-dessus du seuil est du calcul pur perdu.
      //
      // Les candidats scelles gardent une p-value estimee sur les tirages
      // qu'ils ont vus. Elle reste au-dessus du seuil, donc se classe apres
      // toutes les p-values retenues : **les rangs BH des candidats qui passent
      // sont inchanges**, et le verdict aussi.
      const seuilExtremes = Math.floor(options.seuilQ * (B + 1))
      let actifs = candidats.map((_, k) => k)
      let fenetresActives = toutesFenetres.map((_, i) => i)

      for (let b = 0; b < B && actifs.length > 0; b++) {
        const decalage = alea() * T
        const decales = decalerEpisodes(episodesTries, decalage, t0, T)
        const maxFinP = prefixeMaxFin(decales)

        const issuesP: (IssuesSymptome | null)[] = new Array(toutesFenetres.length).fill(null)
        for (const i of fenetresActives) {
          const fenetre = toutesFenetres[i]!
          const iss = calculerIssues(
            tRepas,
            decales,
            maxFinP,
            fenetre.debutH * HEURE_MS,
            fenetre.finH * HEURE_MS,
            maintenant,
          )
          if (iss.nbEligibles > 0) issuesP[i] = iss
        }

        let scelle = false
        for (const k of actifs) {
          const l = candidats[k]!
          tirages[k]!++
          let max = 0
          for (const i of fenetresDuMax[k]!) {
            const iss = issuesP[i]
            if (!iss) continue
            const d = Math.abs(differenceRisque(compter(l.expo, iss).contingence))
            if (d > max) max = d
          }
          if (max >= l.statObs && ++extremes[k]! >= seuilExtremes) scelle = true
        }

        if (scelle) {
          actifs = actifs.filter((k) => extremes[k]! < seuilExtremes)
          fenetresActives = [
            ...new Set(actifs.flatMap((k) => fenetresDuMax[k]!)),
          ]
        }
      }

      candidats.forEach((l, k) => {
        // (extremes + 1) / (tirages + 1) : estimateur non biaise, jamais nul.
        l.pPermutation = (extremes[k]! + 1) / (tirages[k]! + 1)
      })
    }
  }

  // --- Phase C : correction de multiplicite, sur UNE seule famille ---
  //
  // La famille est l'ensemble des couples (exposition, symptome) reellement
  // testes — tous symptomes et tous niveaux d'agregation confondus. C'est bien
  // ce que l'utilisateur parcourt : une liste unique de pistes.
  //
  // Les fenetres n'y ajoutent **aucun** test : elles sont absorbees par la
  // statistique du maximum (phase A), dont la loi nulle par permutation prend le
  // meme maximum. Les compter comme quatre tests distincts quadruplerait la
  // famille sans raison — et rendrait le verdict inatteignable, la q-value de
  // permutation etant bornee par le rapport entre taille de famille et nombre
  // de tirages.
  //
  // Corriger separement par symptome et par niveau, comme une premiere version
  // le faisait, revient a constituer une vingtaine de familles independantes
  // chacune controlee a 5 % : le taux de fausses pistes par jeu de donnees
  // grimpe alors a ~40 % (mesure sur jeux nuls). Le decoupage etait defendable
  // en theorie, il ne l'est pas en pratique ici.
  //
  // Les couples exploitables non pre-filtres entrent dans la famille des
  // permutations avec p = 1 : la taille de famille reste celle de tous les
  // tests menes, ce qui est le comportement conservateur recherche.
  const exploitables = lignes.filter((l) => l.exploitable)
  const qFisherParLigne = new Map<Ligne, number>()
  const qPermParLigne = new Map<Ligne, number>()

  if (exploitables.length > 0) {
    const qsF = benjaminiHochberg(exploitables.map((l) => l.pFisher))
    exploitables.forEach((l, i) => qFisherParLigne.set(l, qsF[i]!))

    // Les couples exploitables non pre-filtres entrent dans la famille des
    // permutations avec p = 1 : la taille de famille reste celle de tous les
    // tests menes, ce qui est le comportement conservateur recherche.
    //
    // Ce rembourrage n'est pas cosmetique, et l'essayer sans l'a montre : reduit
    // a la seule sous-famille des candidats permutes, le taux de fausses pistes
    // sur jeux nuls passe de 1/10 a 4/10 — les expositions au niveau du jour
    // (facteurs de vie) et les charges glissantes, fortement auto-correlees,
    // sont les premieres a franchir le seuil.
    //
    // Sa contrepartie est un couplage entre la taille de la famille et le
    // plancher `1/(B+1)` de la p-value de permutation, traite en phase B.
    if (exploitables.some((l) => l.pPermutation !== null)) {
      const qsP = benjaminiHochberg(exploitables.map((l) => l.pPermutation ?? 1))
      exploitables.forEach((l, i) => {
        if (l.pPermutation !== null) qPermParLigne.set(l, qsP[i]!)
      })
    }
  }

  // --- Phase D : assemblage ---
  const associations: Association[] = []

  /** Grandeurs derivees d'une mesure, communes a l'association et au profil. */
  const chiffrer = (m: Mesure) => {
    const ct = m.compte.contingence
    const { rExp, rNon, nExp } = risques(ct)
    return {
      contingence: ct,
      repasExposes: nExp,
      repasExploitables: m.issues.nbEligibles,
      episodesDistincts: m.compte.episodes.size,
      risqueExpose: rExp,
      risqueNonExpose: rNon,
      risqueRelatif: rNon > 0 ? rExp / rNon : rExp > 0 ? Infinity : 1,
      icRisqueExpose: intervalleWilson(ct.a, nExp),
    }
  }

  for (const l of lignes) {
    const base = chiffrer(l.meilleure)

    const assoc: Association = {
      cle: l.expo.cle,
      libelle: l.expo.libelle,
      niveau: l.expo.niveau,
      symptome: l.symptome,
      fenetre: l.meilleure.fenetre,
      profil: l.mesures.map((m) => ({
        fenetre: m.fenetre,
        ...chiffrer(m),
        pFisher: m.pFisher,
        exploitable: m.exploitable,
        retenue: m.exploitable,
      })),
      ...base,
      pFisher: l.pFisher,
      qFisher: qFisherParLigne.get(l) ?? 1,
      pPermutation: l.pPermutation,
      qPermutation: qPermParLigne.get(l) ?? null,
      verdict: 'donnees-insuffisantes',
      indissociablesDe: [],
    }
    if (l.motif) assoc.motifInsuffisance = l.motif

    assoc.verdict = deciderVerdict(assoc, l.exploitable, options)

    if (assoc.verdict === 'piste-serieuse' || assoc.verdict === 'a-surveiller') {
      // Stratification et effet-dose se lisent dans la fenetre retenue : c'est
      // celle qui porte le signal, les autres n'ont rien a expliquer.
      const iss = l.meilleure.issues
      const strates = stratifierParStress(l.expo, repasTries, iss, stressParJour, options)
      if (strates) assoc.stratesStress = strates
      // Charges glissantes incluses : leur `charges[]` porte deja la valeur
      // cumulee, l'effet-dose s'y lit donc directement.
      if (l.expo.niveau === 'famille' || l.expo.niveau === 'charge') {
        const dose = analyserDose(l.expo, iss)
        if (dose) assoc.dose = dose
      }
    }

    associations.push(assoc)
  }

  const blocs = detecterBlocsIndissociables(expositions, options.seuilJaccard)
  const appartenance = new Map<string, string[]>()
  for (const bloc of blocs) {
    for (const cle of bloc.cles) {
      appartenance.set(cle, bloc.cles.filter((c) => c !== cle))
    }
  }
  for (const a of associations) {
    a.indissociablesDe = appartenance.get(a.cle) ?? []
  }

  associations.sort(comparerAssociations)

  return {
    genereLe: maintenant,
    options,
    qualite: evaluerQualite(repasTries, episodes, entree.journaux, maintenant, options, {
      permutations: permutationsUtilisees,
      plafondAtteint: plafondPermutationsAtteint,
    }),
    associations,
    blocsIndissociables: blocs,
  }
}

// ---------------------------------------------------------------------------
// Verdict
// ---------------------------------------------------------------------------

function deciderVerdict(
  a: Association,
  exploitable: boolean,
  o: OptionsAnalyse,
): Verdict {
  if (!exploitable) return 'donnees-insuffisantes'

  const augmente = a.risqueRelatif > 1
  const assezEpisodes = a.episodesDistincts >= o.minEpisodesDistincts

  // Trois exigences cumulatives, chacune contre un mode d'echec distinct :
  //  - qFisher   : la multiplicite des tests ;
  //  - qPermutation : l'auto-correlation des symptomes ET la multiplicite,
  //                   sur une statistique qui ne suppose pas l'independance ;
  //  - episodes distincts : qu'une poignee de poussees ne porte pas tout le signal.
  if (
    augmente &&
    assezEpisodes &&
    a.qFisher <= o.seuilQ &&
    a.qPermutation !== null &&
    a.qPermutation <= o.seuilQ
  ) {
    return 'piste-serieuse'
  }

  if (augmente && a.pFisher <= 0.05) return 'a-surveiller'

  return 'probablement-non-lie'
}

const ORDRE_VERDICT: Record<Verdict, number> = {
  'piste-serieuse': 0,
  'a-surveiller': 1,
  'probablement-non-lie': 2,
  'donnees-insuffisantes': 3,
}

function comparerAssociations(x: Association, y: Association): number {
  const dv = ORDRE_VERDICT[x.verdict] - ORDRE_VERDICT[y.verdict]
  if (dv !== 0) return dv
  const dq = x.qFisher - y.qFisher
  if (dq !== 0) return dq
  return y.risqueRelatif - x.risqueRelatif
}

// ---------------------------------------------------------------------------
// Stratification par stress
// ---------------------------------------------------------------------------

/**
 * Rejoue l'association separement les jours de stress bas et eleve.
 *
 * Une association qui tient dans les deux strates est credible. Une association
 * qui n'apparait que les jours de stress eleve signale surtout que le stress
 * fait manger differemment — ce n'est pas la meme conclusion.
 */
function stratifierParStress(
  expo: Exposition,
  repasTries: readonly Meal[],
  issues: IssuesSymptome,
  stressParJour: Map<string, number>,
  o: OptionsAnalyse,
): Stratification[] | undefined {
  const valeurs: number[] = []
  for (let i = 0; i < repasTries.length; i++) {
    if (!issues.eligible[i]) continue
    const s = stressParJour.get(dayKeyOf(repasTries[i]!.at))
    if (s !== undefined) valeurs.push(s)
  }
  if (valeurs.length < 2 * o.minRepasExposes) return undefined

  const seuil = mediane(valeurs)
  const exposeSet = new Set(expo.repasIdx)

  const strates: Stratification[] = []
  for (const haut of [false, true]) {
    const ct: Contingence = { a: 0, b: 0, c: 0, d: 0 }
    for (let i = 0; i < repasTries.length; i++) {
      if (!issues.eligible[i]) continue
      const s = stressParJour.get(dayKeyOf(repasTries[i]!.at))
      if (s === undefined) continue
      if (haut ? s <= seuil : s > seuil) continue

      const expose = exposeSet.has(i)
      const declenche = issues.onset[i] === 1
      if (expose && declenche) ct.a++
      else if (expose) ct.b++
      else if (declenche) ct.c++
      else ct.d++
    }
    const { rExp, rNon } = risques(ct)
    strates.push({
      libelle: haut ? `Stress élevé (> ${seuil.toFixed(0)}/10)` : `Stress bas (<= ${seuil.toFixed(0)}/10)`,
      contingence: ct,
      risqueRelatif: rNon > 0 ? rExp / rNon : rExp > 0 ? Infinity : 1,
      exploitable: ct.a + ct.b >= o.minRepasExposes && ct.a >= 1,
    })
  }

  return strates
}

// ---------------------------------------------------------------------------
// Effet-dose
// ---------------------------------------------------------------------------

/**
 * Compare la charge portee par les repas suivis d'un declenchement a celle des
 * autres, et decoupe en tertiles.
 *
 * Une relation dose-reponse monotone est un argument nettement plus fort qu'une
 * simple association binaire, et colle a la realite du SCI : c'est la quantite
 * cumulee de FODMAP sur un repas qui compte, plus que la presence d'un aliment.
 */
function analyserDose(expo: Exposition, issues: IssuesSymptome): ResultatDose | undefined {
  const n = issues.eligible.length
  const chargeParRepas = new Float64Array(n)
  expo.repasIdx.forEach((idx, k) => {
    chargeParRepas[idx] = expo.charges[k] ?? 0
  })

  const avec: number[] = []
  const sans: number[] = []
  const toutes: number[] = []
  for (let i = 0; i < n; i++) {
    if (!issues.eligible[i]) continue
    const c = chargeParRepas[i]!
    toutes.push(c)
    if (issues.onset[i]) avec.push(c)
    else sans.push(c)
  }
  if (avec.length < 3 || sans.length < 3) return undefined

  const mw = mannWhitney(avec, sans)

  const t1 = quantile(toutes, 1 / 3)
  const t2 = quantile(toutes, 2 / 3)
  const effectifs: [number, number, number] = [0, 0, 0]
  const evenements: [number, number, number] = [0, 0, 0]
  for (let i = 0; i < n; i++) {
    if (!issues.eligible[i]) continue
    const c = chargeParRepas[i]!
    const t = c <= t1 ? 0 : c <= t2 ? 1 : 2
    effectifs[t]++
    if (issues.onset[i]) evenements[t]++
  }

  const taux: [number, number, number] = [
    effectifs[0] ? evenements[0] / effectifs[0] : 0,
    effectifs[1] ? evenements[1] / effectifs[1] : 0,
    effectifs[2] ? evenements[2] / effectifs[2] : 0,
  ]

  return {
    p: mw.p,
    auc: mw.auc,
    tauxParTertile: taux,
    effectifsParTertile: effectifs,
    monotone: taux[0] <= taux[1] && taux[1] <= taux[2],
  }
}

// ---------------------------------------------------------------------------
// Expositions indissociables
// ---------------------------------------------------------------------------

/**
 * Regroupe les expositions dont les ensembles de repas se recouvrent presque
 * entierement (indice de Jaccard).
 *
 * Si l'ail et l'oignon apparaissent toujours ensemble, leurs statistiques sont
 * identiques et **aucune donnee ne permet de les departager**. Le dire est plus
 * utile que d'afficher deux « pistes » qui n'en sont qu'une.
 */
export function detecterBlocsIndissociables(
  expositions: readonly Exposition[],
  seuil: number,
): { cles: string[]; libelles: string[] }[] {
  // Recettes et charges glissantes incluses.
  //
  // Une recette dominee par un seul ingredient lui est indissociable. Et deux
  // charges glissantes le sont tout autant : le cumul sur 48 h contient celui
  // sur 24 h, et deux familles portees par les memes aliments (le fructose et
  // les polyols de la pomme) bougent ensemble. Mesure sur jeux nuls : les
  // fausses pistes arrivent precisement par paires de ce type — `charge:
  // fructose-24h` avec `charge:polyols-24h`, ou une charge avec l'aliment qui
  // la porte. Les signaler comme un seul bloc evite de lire un artefact unique
  // comme deux resultats concordants, ce qui est bien pire que de le lire une
  // fois.
  const candidats = expositions.filter(
    (e) =>
      (e.niveau === 'ingredient' || e.niveau === 'recette' || e.niveau === 'charge') &&
      e.repasIdx.length >= 3,
  )
  const sets = candidats.map((e) => new Set(e.repasIdx))

  // Union-find sur les paires suffisamment similaires.
  const parent = candidats.map((_, i) => i)
  const trouver = (i: number): number => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]!]!
      i = parent[i]!
    }
    return i
  }
  const unir = (i: number, j: number) => {
    const a = trouver(i)
    const b = trouver(j)
    if (a !== b) parent[b] = a
  }

  for (let i = 0; i < candidats.length; i++) {
    for (let j = i + 1; j < candidats.length; j++) {
      const si = sets[i]!
      const sj = sets[j]!
      let inter = 0
      const [petit, grand] = si.size <= sj.size ? [si, sj] : [sj, si]
      for (const v of petit) if (grand.has(v)) inter++
      const union = si.size + sj.size - inter
      if (union > 0 && inter / union >= seuil) unir(i, j)
    }
  }

  const groupes = new Map<number, number[]>()
  for (let i = 0; i < candidats.length; i++) {
    const r = trouver(i)
    const g = groupes.get(r)
    if (g) g.push(i)
    else groupes.set(r, [i])
  }

  return [...groupes.values()]
    .filter((g) => g.length > 1)
    .map((g) => ({
      cles: g.map((i) => candidats[i]!.cle),
      libelles: g.map((i) => candidats[i]!.libelle),
    }))
}

// ---------------------------------------------------------------------------
// Qualite des donnees
// ---------------------------------------------------------------------------

function evaluerQualite(
  repasTries: readonly Meal[],
  episodes: readonly SymptomEpisode[],
  journaux: readonly DailyLog[],
  maintenant: number,
  o: OptionsAnalyse,
  perm: { permutations: number; plafondAtteint: boolean },
): QualiteDonnees {
  const avertissements: string[] = []

  if (perm.plafondAtteint) {
    avertissements.push(
      "Trop de pistes candidates pour le nombre de permutations disponible : le test de permutation ne peut plus atteindre le seuil, et aucune piste ne sera declaree serieuse tant que ce sera le cas. Reduire la periode analysee ou le nombre d'aliments distincts.",
    )
  }

  const joursAvecRepas = new Set(repasTries.map((m) => dayKeyOf(m.at)))
  const joursAvecJournal = new Set(journaux.map((j) => j.date))

  const premier = repasTries.length ? epoch(repasTries[0]!.at) : maintenant
  const nbJoursSuivis = Math.max(1, Math.round((maintenant - premier) / JOUR_MS))

  // La fenetre la plus longue commande : un repas est « trop recent » des lors
  // qu'au moins une fenetre n'est pas echue. Avec la fenetre 24-72 h, cela
  // represente desormais environ trois jours de repas — ils ne sont pas perdus,
  // seulement pas encore jugeables.
  const finMaxH = Math.max(...o.fenetres.map((f) => f.finH))
  const w1 = finMaxH * HEURE_MS
  const repasTropRecents = repasTries.filter((m) => epoch(m.at) + w1 > maintenant).length

  // Score heuristique : volume de repas, volume d'episodes, regularite de saisie.
  const sVolume = Math.min(1, repasTries.length / 200)
  const sEpisodes = Math.min(1, episodes.length / 20)
  const sRegularite = Math.min(1, joursAvecRepas.size / Math.max(1, nbJoursSuivis))
  const score = Math.round(100 * (0.4 * sVolume + 0.3 * sEpisodes + 0.3 * sRegularite))

  if (repasTries.length < 60) {
    avertissements.push(
      `Seulement ${repasTries.length} repas enregistres. En dessous d'environ 200 repas, seuls les niveaux « famille FODMAP » et « categorie » ont une chance d'atteindre la significativite.`,
    )
  }
  if (episodes.length < 10) {
    avertissements.push(
      `Seulement ${episodes.length} episode(s) de symptomes. Sans declenchements, il n'y a rien a correler.`,
    )
  }
  if (joursAvecRepas.size < nbJoursSuivis * 0.7) {
    avertissements.push(
      `Des repas ne sont saisis que ${joursAvecRepas.size} jour(s) sur ${nbJoursSuivis}. Les jours non saisis ne comptent pas comme « sans symptome » : ils sont simplement ignores, ce qui reduit la puissance de l'analyse.`,
    )
  }
  if (joursAvecJournal.size < nbJoursSuivis * 0.3) {
    avertissements.push(
      'Le journal stress / sommeil est peu rempli : la stratification qui permet d\'ecarter les fausses pistes ne pourra pas etre calculee.',
    )
  }

  return {
    nbRepas: repasTries.length,
    nbEpisodes: episodes.length,
    nbJoursSuivis,
    nbJoursAvecRepas: joursAvecRepas.size,
    nbJoursAvecJournal: joursAvecJournal.size,
    repasTropRecents,
    score,
    avertissements,
  }
}

export type { Association, ResultatAnalyse, OptionsAnalyse, SymptomType }
